import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ShieldAlert, RotateCcw, Eye, Mail, Send, Loader2, Activity } from "lucide-react";
import { toast } from "sonner";
import { LoginAliasesPanel } from "../components/LoginAliasesPanel";

const ROLE_TONE = {
  dn: "bg-purple-100 text-purple-800",
  dr: "bg-blue-100 text-blue-800",
  dz: "bg-cyan-100 text-cyan-800",
  consultant: "bg-emerald-100 text-emerald-800",
  president: "bg-amber-100 text-amber-800",
};

function fmtTs(iso, locale) {
  try {
    return new Date(iso).toLocaleString(locale === "en" ? "en-GB" : "fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

export default function AuditLogPage() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [targetId, setTargetId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (since) params.since = new Date(since).toISOString();
      if (until) params.until = new Date(until).toISOString();
      if (targetId.trim()) params.impersonated_user_id = targetId.trim();
      const { data } = await api.get("/admin/audit-logs/impersonation", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [since, until, targetId]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setSince("");
    setUntil("");
    setTargetId("");
  };

  // ------------------ Nudge sub-panel ------------------
  const [lastRun, setLastRun] = useState(null);
  const [sendingNudge, setSendingNudge] = useState(false);
  const [costs, setCosts] = useState(null);

  useEffect(() => {
    api.get("/admin/diagnostics/costs").then((r) => setCosts(r.data)).catch(() => {});
  }, []);

  const loadLastRun = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/nudges/connexion/last");
      setLastRun(data.last_run);
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => { loadLastRun(); }, [loadLastRun]);

  const sendNudge = async (dryRun) => {
    setSendingNudge(true);
    try {
      const { data } = await api.post(`/admin/nudges/connexion/send?dry_run=${dryRun}`);
      toast.success(t("audit.nudge_sent", { n: data.coaches_targeted, sent: data.sent }));
      await loadLastRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSendingNudge(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <section className="bg-gradient-to-br from-apollo-orange-light/60 via-white to-bg-soft border border-border rounded-2xl p-6 md:p-8">
        <p className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2">
          {t("audit.eyebrow")}
        </p>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter text-text-primary mb-2 flex items-center gap-3">
          <ShieldAlert className="w-8 h-8 text-apollo-orange" />
          {t("audit.title")}
        </h1>
        <p className="font-oracle italic text-lg text-text-primary/80 max-w-3xl">
          {t("audit.subtitle")}
        </p>
      </section>

      <section className="bg-white border border-border rounded-xl p-4 md:p-6" data-testid="audit-costs-panel">
        <div className="flex items-center gap-3 mb-2">
          <Activity className="w-5 h-5 text-apollo-orange" />
          <h2 className="font-heading font-bold text-lg tracking-tight">{t("audit.costs_title")}</h2>
        </div>
        {costs && (
          <>
            <div className="flex flex-wrap gap-4 mb-4 text-sm" data-testid="audit-costs-totals">
              <span><span className="text-text-secondary">{t("audit.costs_total")} : </span><span className="font-bold">${costs.total_cost_usd}</span></span>
              <span><span className="text-text-secondary">{t("audit.costs_month")} : </span><span className="font-bold">${costs.month_cost_usd}</span></span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-text-secondary border-b border-border">
                    <th className="px-3 py-2">{t("audit.costs_date")}</th>
                    <th className="px-3 py-2">{t("audit.costs_group")}</th>
                    <th className="px-3 py-2">{t("audit.costs_model")}</th>
                    <th className="px-3 py-2">{t("audit.costs_provider")}</th>
                    <th className="px-3 py-2">{t("audit.costs_tokens")}</th>
                    <th className="px-3 py-2">{t("audit.costs_cost")}</th>
                  </tr>
                </thead>
                <tbody data-testid="audit-costs-rows">
                  {costs.rows.slice(0, 15).map((r) => (
                    <tr key={r.id} className="border-b border-border/50">
                      <td className="px-3 py-2 text-xs">{fmtTs(r.generated_at, i18n.language)}</td>
                      <td className="px-3 py-2 font-medium">{r.groupe_nom}</td>
                      <td className="px-3 py-2 text-xs font-mono">{r.model_used}</td>
                      <td className="px-3 py-2 text-xs font-mono">{r.ai_provider || "n/a"}</td>
                      <td className="px-3 py-2 text-xs">{r.input_tokens} / {r.output_tokens}</td>
                      <td className="px-3 py-2 font-bold">${(r.cost_usd || 0).toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <LoginAliasesPanel />

      <section className="bg-white border border-border rounded-xl p-4 md:p-6" data-testid="audit-nudge-panel">
        <div className="flex items-center gap-3 mb-2">
          <Mail className="w-5 h-5 text-apollo-orange" />
          <h2 className="font-heading font-bold text-lg tracking-tight">{t("audit.nudge_title")}</h2>
        </div>
        <p className="text-sm text-text-secondary mb-4">{t("audit.nudge_subtitle")}</p>
        {lastRun && (
          <div className="text-xs bg-bg-soft border border-border rounded-md p-3 mb-4" data-testid="audit-nudge-last">
            <span className="text-text-secondary">{t("audit.nudge_last")} : </span>
            <span className="font-mono">{fmtTs(lastRun.started_at, i18n.language)}</span>
            <span className="mx-2 text-text-secondary">·</span>
            <span className="font-medium text-text-primary">
              {t("audit.nudge_targeted", { n: lastRun.coaches_targeted, sent: lastRun.sent })}
            </span>
            <span className="mx-2 text-text-secondary">·</span>
            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${lastRun.brevo_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {lastRun.brevo_configured ? t("audit.nudge_brevo_ok") : t("audit.nudge_brevo_stub")}
            </span>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => sendNudge(true)}
            variant="outline"
            disabled={sendingNudge}
            data-testid="audit-nudge-dryrun"
          >
            {sendingNudge ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {t("audit.nudge_dryrun")}
          </Button>
          <Button
            onClick={() => sendNudge(false)}
            disabled={sendingNudge}
            data-testid="audit-nudge-send"
          >
            {sendingNudge ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {t("audit.nudge_send_now")}
          </Button>
        </div>
      </section>

      <section className="bg-white border border-border rounded-xl p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs uppercase tracking-wider text-text-secondary font-semibold block mb-1">
              {t("audit.from")}
            </label>
            <Input
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              data-testid="audit-filter-since"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-text-secondary font-semibold block mb-1">
              {t("audit.to")}
            </label>
            <Input
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              data-testid="audit-filter-until"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-text-secondary font-semibold block mb-1">
              {t("audit.target_id")}
            </label>
            <Input
              placeholder="mbr-dn-marie"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              data-testid="audit-filter-target"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={load} data-testid="audit-refresh-btn" className="flex-1">
              {t("audit.refresh")}
            </Button>
            <Button variant="outline" onClick={reset} data-testid="audit-reset-btn" aria-label={t("audit.reset")}>
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-text-secondary mt-3">
          {t("audit.total_count", { count: total })}
        </p>
      </section>

      <section className="bg-white border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-text-secondary" data-testid="audit-loading">{t("audit.loading")}</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-text-secondary" data-testid="audit-empty">
            <Eye className="w-10 h-10 mx-auto text-text-secondary/40 mb-3" />
            {t("audit.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-bg-soft text-xs uppercase tracking-wider text-text-secondary">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">{t("audit.col_time")}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t("audit.col_ceo")}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t("audit.col_target")}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t("audit.col_endpoint")}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t("audit.col_status")}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t("audit.col_ip")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-bg-soft/50" data-testid={`audit-row-${r.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-text-primary">
                      {fmtTs(r.timestamp, i18n.language)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-text-primary">{r.real_user_full_name || r.real_user_email}</div>
                      <div className="text-xs text-text-secondary">{r.real_user_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text-primary">{r.impersonated_user_full_name || r.impersonated_user_id}</span>
                        {r.impersonated_role && (
                          <span className={`inline-block text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${ROLE_TONE[r.impersonated_role] || "bg-gray-100 text-gray-700"}`}>
                            {r.impersonated_role}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-text-secondary">{r.impersonated_user_email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <span className="inline-block bg-bg-soft text-text-primary px-1.5 py-0.5 rounded mr-1">{r.method}</span>
                      <span className="text-text-primary">{r.path}</span>
                      {r.query && <span className="text-text-secondary">?{r.query}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded ${r.status_code < 300 ? "bg-emerald-100 text-emerald-800" : r.status_code < 400 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>
                        {r.status_code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-text-secondary font-mono">{r.ip || "n/a"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
